import { _ as _assign } from './tslib.es6-c6da0ae6.js';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import classNames from 'classnames';
import React, { memo, useMemo, Fragment, useState, useEffect, useRef, useCallback } from 'react';
import useMobileView from './useMobileView/index.js';
import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { BreakPoints } from '@picsart/design-system/foundation/responsive';
import { mediaQuery } from './mediaHelper/index.js';
import { IconArrowLeftFilled } from '@picsart/design-system/foundation/icons/IconArrowLeftFilled';
import { IconArrowRightFilled } from '@picsart/design-system/foundation/icons/IconArrowRightFilled';
import { PositiveColors, PrimaryColors } from '@picsart/design-system/foundation/colors';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { c as createCommonjsModule, g as getDefaultExportFromCjs, a as _typeof_1, b as getAugmentedNamespace, d as commonjsGlobal } from './typeof-c772fc8d.js';

var _a, _b, _c;
var IMAGE_TYPES = ['webp', 'png'];
var observerOptions = {
  root: null,
  rootMargin: '0px 0px 150px 0px',
  threshold: 0.01
};
var bleedModeOptions = {
  large: (_a = {
    padding: Spacings.s72
  }, _a[mediaQuery(BreakPoints.Small)] = {
    padding: Spacings.s48
  }, _a),
  medium: (_b = {
    padding: Spacings.s48
  }, _b[mediaQuery(BreakPoints.Small)] = {
    padding: Spacings.s24
  }, _b),
  small: (_c = {
    padding: Spacings.s16
  }, _c[mediaQuery(BreakPoints.Small)] = {
    padding: Spacings.s4
  }, _c),
  none: {
    padding: Spacings.s0
  },
  stretch: {
    padding: Spacings.s0
  }
};
var FittingOptions;
(function (FittingOptions) {
  FittingOptions["Contain"] = "contain";
  FittingOptions["Cover"] = "cover";
  FittingOptions["Fill"] = "fill";
  FittingOptions["None"] = "none";
})(FittingOptions || (FittingOptions = {}));

var useStyles$4 = createUseStyles({
  root: function root(_a) {
    var padding = _a.padding,
      objectFit = _a.objectFit;
    return _assign(_assign({
      width: '100%',
      maxHeight: '100%',
      height: padding === 'stretch' ? '100%' : 'auto',
      backgroundColor: 'transparent',
      overflow: 'hidden'
    }, bleedModeOptions[padding || 'none']), {
      '& img, & video': {
        objectFit: objectFit,
        "float": 'left'
      }
    });
  }
}, {
  name: 'media'
});

var testIds = {
  mediaRoot: 'media-root',
  imageRoot: 'image-root',
  video: 'media-video'
};

var useStyles$3 = createUseStyles(function (_a) {
  var padding = _a.padding;
  return {
    root: _assign(_assign({
      maxHeight: '100%',
      height: function height(props) {
        return props.padding === 'stretch' ? '100%' : 'auto';
      }
    }, bleedModeOptions[padding || 'none']), {
      '& img': {
        objectFit: function objectFit(props) {
          return props.objectFit;
        }
      }
    })
  };
}, {
  name: 'base-image'
});

var createCMSImageUrl = function createCMSImageUrl(imgUrl, type, size, quality) {
  if (size === void 0) {
    size = 480;
  }
  var baseUrl = "".concat(imgUrl, "?type=").concat(type, "&r=").concat(size, "&to=min");
  return quality ? "".concat(baseUrl, "&q=").concat(quality) : baseUrl;
};
var BaseImage = function BaseImage(_a) {
  var className = _a.className,
    imageClassName = _a.imageClassName,
    imageSrc = _a.imageSrc,
    imageSrcLowRes = _a.imageSrcLowRes,
    mobileImageSrc = _a.mobileImageSrc,
    height = _a.height,
    mobileHeight = _a.mobileHeight,
    _b = _a.padding,
    padding = _b === void 0 ? 'none' : _b,
    _c = _a.alt,
    alt = _c === void 0 ? '' : _c,
    _d = _a.objectFit,
    objectFit = _d === void 0 ? 'Contain' : _d,
    quality = _a.quality,
    _e = _a.appendCDNQuery,
    appendCDNQuery = _e === void 0 ? true : _e,
    priority = _a.priority,
    isMultiply = _a.isMultiply;
  var isMobile = useMobileView().isMobile;
  var imgHeight = useMemo(function () {
    return (isMobile ? mobileHeight : height) || '100%';
  }, [isMobile, mobileHeight, height]);
  var root = useStyles$3({
    padding: padding,
    objectFit: objectFit,
    imgHeight: imgHeight
  }).root;
  var imgUrls = useMemo(function () {
    return {
      lowRes: isMobile && mobileImageSrc ? mobileImageSrc : imageSrcLowRes || imageSrc,
      hiRes: isMobile && mobileImageSrc ? mobileImageSrc : imageSrc
    };
  }, [imageSrc, isMobile, imageSrcLowRes, mobileImageSrc]);
  var getCustomHeight = function getCustomHeight(customHeight, height, isMultiply) {
    if (isMultiply === void 0) {
      isMultiply = true;
    }
    var multiplyer = isMultiply ? 2 : 1;
    return height ? height * multiplyer : customHeight;
  };
  var placeholderImage = appendCDNQuery ? createCMSImageUrl(imgUrls.hiRes, 'png', getCustomHeight(640, height, false), quality) : imgUrls.hiRes;
  return jsx("div", _assign({
    "data-testid": testIds.imageRoot,
    className: classNames(root, className)
  }, {
    children: jsxs("picture", {
      children: [IMAGE_TYPES.map(function (type) {
        return jsxs(Fragment, {
          children: [jsx("source", {
            srcSet: "\n                    ".concat(appendCDNQuery ? createCMSImageUrl(imgUrls.lowRes, type, getCustomHeight(400, mobileHeight, false), quality) : imgUrls.lowRes, ",\n                    ").concat(appendCDNQuery ? createCMSImageUrl(imgUrls.hiRes, type, getCustomHeight(800, mobileHeight), quality) : imgUrls.hiRes, " 2x\n                  "),
            media: "(max-width: 767px)",
            type: "image/".concat(type)
          }), jsx("source", {
            srcSet: "\n                    ".concat(appendCDNQuery ? createCMSImageUrl(imgUrls.lowRes, type, getCustomHeight(640, height, false), quality) : imgUrls.lowRes, ",\n                    ").concat(appendCDNQuery ? createCMSImageUrl(imgUrls.hiRes, type, getCustomHeight(1280, height, isMultiply !== null && isMultiply !== void 0 ? isMultiply : true), quality) : imgUrls.hiRes, " 2x\n                  "),
            media: "(min-width: 768px)",
            type: "image/".concat(type)
          })]
        }, "source-".concat(type));
      }), jsx("img", {
        loading: priority ? 'eager' : 'lazy',
        alt: alt,
        width: "100%",
        height: imgHeight,
        src: placeholderImage,
        className: imageClassName
      }, "main-image")]
    })
  }));
};
var BaseImage$1 = /*#__PURE__*/memo(BaseImage);

var useStyles$2 = createUseStyles(function () {
  return {
    media: {
      transition: 'all 200ms ease-in',
      objectFit: 'cover'
    },
    placeholder: {
      opacity: 0.2
    }
  };
}, {
  name: 'media'
});

var BaseVideo = function BaseVideo(_a) {
  var mobileWebmVideoSrc = _a.mobileWebmVideoSrc,
    webmVideoSrc = _a.webmVideoSrc,
    videoSrc = _a.videoSrc,
    mobileVideoSrc = _a.mobileVideoSrc,
    poster = _a.poster,
    isControls = _a.isControls,
    _b = _a.isIntersected,
    isIntersected = _b === void 0 ? false : _b;
  var isMobile = useMobileView().isMobile;
  var _c = useStyles$2(),
    placeholder = _c.placeholder,
    media = _c.media;
  var _d = useState(''),
    video = _d[0],
    setVideo = _d[1];
  var _e = useState(''),
    webmVideo = _e[0],
    setWebmVideo = _e[1];
  var videoUrl = useMemo(function () {
    if (videoSrc) {
      if (isMobile && mobileVideoSrc) {
        return mobileVideoSrc;
      }
      return videoSrc;
    }
    return null;
  }, [isMobile, mobileVideoSrc, videoSrc]);
  var webmVideoUrl = useMemo(function () {
    if (webmVideoSrc) {
      if (isMobile && mobileWebmVideoSrc) {
        return mobileWebmVideoSrc;
      }
      return webmVideoSrc;
    }
    return null;
  }, [isMobile, mobileWebmVideoSrc, webmVideoSrc]);
  useEffect(function () {
    if (videoUrl) {
      setVideo(videoUrl);
    }
    if (webmVideoUrl) {
      setWebmVideo(webmVideoUrl);
    }
  }, [videoUrl, webmVideoUrl]);
  return jsx(Fragment$1, {
    children: (video || webmVideo) && jsxs("video", _assign({
      controls: isControls,
      "data-testid": "media-video",
      className: classNames(media, !isIntersected && placeholder),
      poster: poster,
      height: "100%",
      width: "100%",
      playsInline: true,
      preload: "none",
      autoPlay: true,
      muted: true
    }, {
      children: [jsx("source", {
        src: webmVideo,
        type: "video/webm"
      }), jsx("source", {
        src: video,
        type: "video/mp4"
      })]
    }))
  });
};
var BaseVideo$1 = /*#__PURE__*/memo(BaseVideo);

var useStyles$1 = createUseStyles(function (_a) {
  var padding = _a.padding;
  return {
    root: _assign({
      height: function height() {
        return padding === 'stretch' ? '100%' : 'auto';
      }
    }, bleedModeOptions[padding || 'none']),
    wrapper: function wrapper(_a) {
      var _b;
      var before = _a.before,
        minHeight = _a.minHeight;
      return _b = {
        width: '100%',
        backgroundImage: "url(".concat(before, ")"),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
        minHeight: minHeight || 420,
        aspectRatio: 1,
        maxHeight: '100%'
      }, _b[mediaQuery(BreakPoints.Small)] = {
        minHeight: 320
      }, _b[mediaQuery(766)] = {
        maxHeight: 'unset'
      }, _b[mediaQuery(500)] = {
        minWidth: 200,
        minHeight: 250
      }, _b;
    },
    afterImage: {
      width: 'auto',
      height: '100%',
      aspectRatio: 1,
      position: 'absolute',
      left: 0,
      top: 0
    },
    slider: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0
    },
    range: {
      width: '100%',
      position: 'absolute',
      background: 'none',
      outline: 'none',
      pointerEvents: 'none',
      zIndex: 3,
      '-webkit-appearance': 'none',
      '&::-webkit-slider-thumb': {
        '-webkit-appearance': 'none',
        pointerEvents: 'auto',
        height: 420,
        width: 20,
        background: 'none',
        cursor: 'pointer'
      }
    },
    imageWrapper: {
      width: function width(_a) {
        var imageWidth = _a.imageWidth;
        return imageWidth;
      },
      height: '100%',
      position: 'absolute',
      left: 0
    },
    container: {
      width: '50%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative'
    },
    dragLine: {
      height: '100%',
      width: 3,
      position: 'absolute',
      left: '50%',
      zIndex: 2,
      '&:before': {
        position: 'absolute',
        content: "''",
        width: '100%',
        height: '100%',
        background: PositiveColors.base["default"],
        top: 0,
        boxShadow: '0 4px 4px rgba(0,0,0,0.25)'
      }
    },
    arrowHolder: {
      width: 46,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: PositiveColors.base["default"],
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      borderRadius: CornerRadius.r12,
      boxShadow: '0 4px 4px rgba(0,0,0,0.25)'
    },
    arrow: {
      fill: PrimaryColors.base["default"]
    }
  };
}, {
  name: 'before-after'
});

var paddings;
(function (paddings) {
  paddings["large"] = "large";
  paddings["small"] = "small";
  paddings["medium"] = "medium";
  paddings["stretch"] = "stretch";
})(paddings || (paddings = {}));

var heights = {
  stretch: 580,
  large: 420,
  medium: 460,
  small: 500
};

function BeforeAfter(_a) {
  var before = _a.before,
    after = _a.after,
    _b = _a.appendCDNQuery,
    appendCDNQuery = _b === void 0 ? true : _b,
    _c = _a.padding,
    padding = _c === void 0 ? paddings.large : _c,
    priority = _a.priority;
  var inputRef = useRef(null);
  var dragLineRef = useRef(null);
  var wrapperRef = useRef(null);
  var _d = useState(0),
    imageWidth = _d[0],
    setImageWidth = _d[1];
  var styles = useStyles$1({
    before: before,
    imageWidth: imageWidth,
    padding: padding,
    minHeight: heights[padding]
  });
  var setStyles = function setStyles(e) {
    if (inputRef.current !== null) {
      inputRef.current.style.width = "".concat(e.target.value, "%");
    }
    if (dragLineRef.current !== null) {
      if (e.target.value === '0') {
        dragLineRef.current.style.left = "".concat(e.target.value - 1, "%");
      } else {
        dragLineRef.current.style.left = "".concat(e.target.value, "%");
      }
    }
  };
  useEffect(function () {
    if (wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) {
      setImageWidth(wrapperRef.current.offsetWidth);
    }
  }, []);
  return jsx("div", _assign({
    className: styles.root
  }, {
    children: jsxs("div", _assign({
      className: styles.wrapper,
      ref: wrapperRef
    }, {
      children: [jsxs("div", _assign({
        className: styles.slider
      }, {
        children: [jsx("div", _assign({
          className: styles.dragLine,
          ref: dragLineRef,
          "data-testid": "before-after-drag-line"
        }, {
          children: jsxs("div", _assign({
            className: styles.arrowHolder
          }, {
            children: [jsx(IconArrowLeftFilled, {
              className: styles.arrow
            }), jsx(IconArrowRightFilled, {
              className: styles.arrow
            })]
          }))
        })), jsx("input", {
          "data-testid": "before-after-range-input",
          type: "range",
          className: styles.range,
          ref: inputRef,
          onChange: setStyles,
          "aria-label": "Range input"
        })]
      })), jsx("div", _assign({
        className: styles.container,
        ref: inputRef
      }, {
        children: jsx("div", _assign({
          className: styles.imageWrapper
        }, {
          children: jsx(BaseImage$1, {
            imageSrc: after,
            className: styles.afterImage,
            objectFit: "Cover",
            appendCDNQuery: appendCDNQuery,
            priority: priority
          })
        }))
      }))]
    }))
  }));
}

var objectWithoutPropertiesLoose = createCommonjsModule(function (module) {
  function _objectWithoutPropertiesLoose(r, e) {
    if (null == r) return {};
    var t = {};
    for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
      if (-1 !== e.indexOf(n)) continue;
      t[n] = r[n];
    }
    return t;
  }
  module.exports = _objectWithoutPropertiesLoose, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var objectWithoutProperties = createCommonjsModule(function (module) {
  function _objectWithoutProperties(e, t) {
    if (null == e) return {};
    var o,
      r,
      i = objectWithoutPropertiesLoose(e, t);
    if (Object.getOwnPropertySymbols) {
      var n = Object.getOwnPropertySymbols(e);
      for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
    }
    return i;
  }
  module.exports = _objectWithoutProperties, module.exports.__esModule = true, module.exports["default"] = module.exports;
});
var _objectWithoutProperties = /*@__PURE__*/getDefaultExportFromCjs(objectWithoutProperties);

var classCallCheck = createCommonjsModule(function (module) {
  function _classCallCheck(a, n) {
    if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function");
  }
  module.exports = _classCallCheck, module.exports.__esModule = true, module.exports["default"] = module.exports;
});
var _classCallCheck = /*@__PURE__*/getDefaultExportFromCjs(classCallCheck);

var toPrimitive_1 = createCommonjsModule(function (module) {
  var _typeof = _typeof_1["default"];
  function toPrimitive(t, r) {
    if ("object" != _typeof(t) || !t) return t;
    var e = t[Symbol.toPrimitive];
    if (void 0 !== e) {
      var i = e.call(t, r || "default");
      if ("object" != _typeof(i)) return i;
      throw new TypeError("@@toPrimitive must return a primitive value.");
    }
    return ("string" === r ? String : Number)(t);
  }
  module.exports = toPrimitive, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var toPropertyKey_1 = createCommonjsModule(function (module) {
  var _typeof = _typeof_1["default"];
  function toPropertyKey(t) {
    var i = toPrimitive_1(t, "string");
    return "symbol" == _typeof(i) ? i : i + "";
  }
  module.exports = toPropertyKey, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var createClass = createCommonjsModule(function (module) {
  function _defineProperties(e, r) {
    for (var t = 0; t < r.length; t++) {
      var o = r[t];
      o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, toPropertyKey_1(o.key), o);
    }
  }
  function _createClass(e, r, t) {
    return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", {
      writable: !1
    }), e;
  }
  module.exports = _createClass, module.exports.__esModule = true, module.exports["default"] = module.exports;
});
var _createClass = /*@__PURE__*/getDefaultExportFromCjs(createClass);

var assertThisInitialized = createCommonjsModule(function (module) {
  function _assertThisInitialized(e) {
    if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    return e;
  }
  module.exports = _assertThisInitialized, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var possibleConstructorReturn = createCommonjsModule(function (module) {
  var _typeof = _typeof_1["default"];
  function _possibleConstructorReturn(t, e) {
    if (e && ("object" == _typeof(e) || "function" == typeof e)) return e;
    if (void 0 !== e) throw new TypeError("Derived constructors may only return object or undefined");
    return assertThisInitialized(t);
  }
  module.exports = _possibleConstructorReturn, module.exports.__esModule = true, module.exports["default"] = module.exports;
});
var _possibleConstructorReturn = /*@__PURE__*/getDefaultExportFromCjs(possibleConstructorReturn);

var getPrototypeOf = createCommonjsModule(function (module) {
  function _getPrototypeOf(t) {
    return module.exports = _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function (t) {
      return t.__proto__ || Object.getPrototypeOf(t);
    }, module.exports.__esModule = true, module.exports["default"] = module.exports, _getPrototypeOf(t);
  }
  module.exports = _getPrototypeOf, module.exports.__esModule = true, module.exports["default"] = module.exports;
});
var _getPrototypeOf = /*@__PURE__*/getDefaultExportFromCjs(getPrototypeOf);

var setPrototypeOf = createCommonjsModule(function (module) {
  function _setPrototypeOf(t, e) {
    return module.exports = _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) {
      return t.__proto__ = e, t;
    }, module.exports.__esModule = true, module.exports["default"] = module.exports, _setPrototypeOf(t, e);
  }
  module.exports = _setPrototypeOf, module.exports.__esModule = true, module.exports["default"] = module.exports;
});

var inherits = createCommonjsModule(function (module) {
  function _inherits(t, e) {
    if ("function" != typeof e && null !== e) throw new TypeError("Super expression must either be null or a function");
    t.prototype = Object.create(e && e.prototype, {
      constructor: {
        value: t,
        writable: !0,
        configurable: !0
      }
    }), Object.defineProperty(t, "prototype", {
      writable: !1
    }), e && setPrototypeOf(t, e);
  }
  module.exports = _inherits, module.exports.__esModule = true, module.exports["default"] = module.exports;
});
var _inherits = /*@__PURE__*/getDefaultExportFromCjs(inherits);

function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
(function () {

  var t = {
      792: function _(t, e, i) {
        i.d(e, {
          Z: function Z() {
            return n;
          }
        });
        var s = i(609),
          o = i.n(s)()(function (t) {
            return t[1];
          });
        o.push([t.id, ':host{--divider-width: 1px;--divider-color: #fff;--divider-shadow: none;--default-handle-width: 50px;--default-handle-color: #fff;--default-handle-opacity: 1;--default-handle-shadow: none;--handle-position-start: 50%;position:relative;display:inline-block;overflow:hidden;line-height:0;direction:ltr}@media screen and (-webkit-min-device-pixel-ratio: 0)and (min-resolution: 0.001dpcm){:host{outline-offset:1px}}:host(:focus){outline:2px solid -webkit-focus-ring-color}::slotted(*){-webkit-user-drag:none;-khtml-user-drag:none;-moz-user-drag:none;-o-user-drag:none;user-drag:none;-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.first{position:absolute;left:0;top:0;right:0;line-height:normal;font-size:100%;max-height:100%;height:100%;width:100%;--exposure: 50%;--keyboard-transition-time: 0ms;--default-transition-time: 0ms;--transition-time: var(--default-transition-time)}.first .first-overlay-container{position:relative;clip-path:inset(0 var(--exposure) 0 0);transition:clip-path var(--transition-time);height:100%}.first .first-overlay{overflow:hidden;height:100%}.first.focused{will-change:clip-path}.first.focused .first-overlay-container{will-change:clip-path}.second{position:relative}.handle-container{transform:translateX(50%);position:absolute;top:0;right:var(--exposure);height:100%;transition:right var(--transition-time),bottom var(--transition-time)}.focused .handle-container{will-change:right}.divider{position:absolute;height:100%;width:100%;left:0;top:0;display:flex;align-items:center;justify-content:center;flex-direction:column}.divider:after{content:" ";display:block;height:100%;border-left-width:var(--divider-width);border-left-style:solid;border-left-color:var(--divider-color);box-shadow:var(--divider-shadow)}.handle{position:absolute;top:var(--handle-position-start);pointer-events:none;box-sizing:border-box;margin-left:1px;transform:translate(calc(-50% - 0.5px), -50%);line-height:0}.default-handle{width:var(--default-handle-width);opacity:var(--default-handle-opacity);transition:all 1s;filter:drop-shadow(var(--default-handle-shadow))}.default-handle path{stroke:var(--default-handle-color)}.vertical .first-overlay-container{clip-path:inset(0 0 var(--exposure) 0)}.vertical .handle-container{transform:translateY(50%);height:auto;top:unset;bottom:var(--exposure);width:100%;left:0;flex-direction:row}.vertical .divider:after{height:1px;width:100%;border-top-width:var(--divider-width);border-top-style:solid;border-top-color:var(--divider-color);border-left:0}.vertical .handle{top:auto;left:var(--handle-position-start);transform:translate(calc(-50% - 0.5px), -50%) rotate(90deg)}', ""]);
        var n = o;
      },
      609: function _(t) {
        t.exports = function (t) {
          var e = [];
          return e.toString = function () {
            return this.map(function (e) {
              var i = t(e);
              return e[2] ? "@media ".concat(e[2], " {").concat(i, "}") : i;
            }).join("");
          }, e.i = function (t, i, s) {
            "string" == typeof t && (t = [[null, t, ""]]);
            var o = {};
            if (s) for (var n = 0; n < this.length; n++) {
              var r = this[n][0];
              null != r && (o[r] = !0);
            }
            for (var a = 0; a < t.length; a++) {
              var d = [].concat(t[a]);
              s && o[d[0]] || (i && (d[2] ? d[2] = "".concat(i, " and ").concat(d[2]) : d[2] = i), e.push(d));
            }
          }, e;
        };
      }
    },
    e = {};
  function i(s) {
    var o = e[s];
    if (void 0 !== o) return o.exports;
    var n = e[s] = {
      id: s,
      exports: {}
    };
    return t[s](n, n.exports, i), n.exports;
  }
  i.n = function (t) {
    var e = t && t.__esModule ? function () {
      return t["default"];
    } : function () {
      return t;
    };
    return i.d(e, {
      a: e
    }), e;
  }, i.d = function (t, e) {
    for (var s in e) i.o(e, s) && !i.o(t, s) && Object.defineProperty(t, s, {
      enumerable: !0,
      get: e[s]
    });
  }, i.o = function (t, e) {
    return Object.prototype.hasOwnProperty.call(t, e);
  }, function () {
    var t = i(792);
    var e = "rendered",
      s = function s(t, e) {
        var i = t.getBoundingClientRect();
        var s, o;
        return "mousedown" === e.type ? (s = e.clientX, o = e.clientY) : (s = e.touches[0].clientX, o = e.touches[0].clientY), s >= i.x && s <= i.x + i.width && o >= i.y && o <= i.y + i.height;
      };
    var o;
    var n = {
        ArrowLeft: -1,
        ArrowRight: 1
      },
      r = ["horizontal", "vertical"],
      a = function a(t) {
        return {
          x: t.touches[0].pageX,
          y: t.touches[0].pageY
        };
      },
      d = function d(t) {
        return {
          x: t.pageX,
          y: t.pageY
        };
      },
      h = "undefined" != typeof window && (null === window || void 0 === window ? void 0 : window.HTMLElement);
    "undefined" != typeof window && (window.document && (o = document.createElement("template"), o.innerHTML = '<div class="second" id="second"> <slot name="second"><slot name="before"></slot></slot> </div> <div class="first" id="first"> <div class="first-overlay"> <div class="first-overlay-container" id="firstImageContainer"> <slot name="first"><slot name="after"></slot></slot> </div> </div> <div class="handle-container"> <div class="divider"></div> <div class="handle" id="handle"> <slot name="handle"> <svg xmlns="http://www.w3.org/2000/svg" class="default-handle" viewBox="-8 -3 16 6"> <path d="M -5 -2 L -7 0 L -5 2 M 5 -2 L 7 0 L 5 2" fill="none" vector-effect="non-scaling-stroke"/> </svg> </slot> </div> </div> </div> '), window.customElements.define("img-comparison-slider", /*#__PURE__*/function (_h) {
      function _class() {
        var _this;
        _classCallCheck(this, _class);
        _this = _callSuper(this, _class), _this.exposure = _this.hasAttribute("value") ? parseFloat(_this.getAttribute("value")) : 50, _this.slideOnHover = !1, _this.slideDirection = "horizontal", _this.keyboard = "enabled", _this.isMouseDown = !1, _this.animationDirection = 0, _this.isFocused = !1, _this.dragByHandle = !1, _this.onMouseMove = function (t) {
          if (_this.isMouseDown || _this.slideOnHover) {
            var _e = d(t);
            _this.slideToPage(_e);
          }
        }, _this.bodyUserSelectStyle = "", _this.bodyWebkitUserSelectStyle = "", _this.onMouseDown = function (t) {
          if (_this.slideOnHover) return;
          if (_this.handle && !s(_this.handleElement, t)) return;
          t.preventDefault(), window.addEventListener("mousemove", _this.onMouseMove), window.addEventListener("mouseup", _this.onWindowMouseUp), _this.isMouseDown = !0, _this.enableTransition();
          var e = d(t);
          _this.slideToPage(e), _this.focus(), _this.bodyUserSelectStyle = window.document.body.style.userSelect, _this.bodyWebkitUserSelectStyle = window.document.body.style.webkitUserSelect, window.document.body.style.userSelect = "none", window.document.body.style.webkitUserSelect = "none";
        }, _this.onWindowMouseUp = function () {
          _this.isMouseDown = !1, window.document.body.style.userSelect = _this.bodyUserSelectStyle, window.document.body.style.webkitUserSelect = _this.bodyWebkitUserSelectStyle, window.removeEventListener("mousemove", _this.onMouseMove), window.removeEventListener("mouseup", _this.onWindowMouseUp);
        }, _this.touchStartPoint = null, _this.isTouchComparing = !1, _this.hasTouchMoved = !1, _this.onTouchStart = function (t) {
          _this.dragByHandle && !s(_this.handleElement, t) || (_this.touchStartPoint = a(t), _this.isFocused && (_this.enableTransition(), _this.slideToPage(_this.touchStartPoint)));
        }, _this.onTouchMove = function (t) {
          if (null === _this.touchStartPoint) return;
          var e = a(t);
          if (_this.isTouchComparing) return _this.slideToPage(e), t.preventDefault(), !1;
          if (!_this.hasTouchMoved) {
            var _i = Math.abs(e.y - _this.touchStartPoint.y),
              _s = Math.abs(e.x - _this.touchStartPoint.x);
            if ("horizontal" === _this.slideDirection && _i < _s || "vertical" === _this.slideDirection && _i > _s) return _this.isTouchComparing = !0, _this.focus(), _this.slideToPage(e), t.preventDefault(), !1;
            _this.hasTouchMoved = !0;
          }
        }, _this.onTouchEnd = function () {
          _this.isTouchComparing = !1, _this.hasTouchMoved = !1, _this.touchStartPoint = null;
        }, _this.onBlur = function () {
          _this.stopSlideAnimation(), _this.isFocused = !1, _this.firstElement.classList.remove("focused");
        }, _this.onFocus = function () {
          _this.isFocused = !0, _this.firstElement.classList.add("focused");
        }, _this.onKeyDown = function (t) {
          if ("disabled" === _this.keyboard) return;
          var e = n[t.key];
          _this.animationDirection !== e && void 0 !== e && (_this.animationDirection = e, _this.startSlideAnimation());
        }, _this.onKeyUp = function (t) {
          if ("disabled" === _this.keyboard) return;
          var e = n[t.key];
          void 0 !== e && _this.animationDirection === e && _this.stopSlideAnimation();
        }, _this.resetDimensions = function () {
          _this.imageWidth = _this.offsetWidth, _this.imageHeight = _this.offsetHeight;
        };
        var e = _this.attachShadow({
            mode: "open"
          }),
          i = document.createElement("style");
        i.innerHTML = t.Z, _this.getAttribute("nonce") && i.setAttribute("nonce", _this.getAttribute("nonce")), e.appendChild(i), e.appendChild(o.content.cloneNode(!0)), _this.firstElement = e.getElementById("first"), _this.handleElement = e.getElementById("handle");
        return _this;
      }
      _inherits(_class, _h);
      return _createClass(_class, [{
        key: "handle",
        get: function get() {
          return this.dragByHandle;
        },
        set: function set(t) {
          this.dragByHandle = "false" !== t.toString().toLowerCase();
        }
      }, {
        key: "value",
        get: function get() {
          return this.exposure;
        },
        set: function set(t) {
          var e = parseFloat(t);
          e !== this.exposure && (this.exposure = e, this.enableTransition(), this.setExposure());
        }
      }, {
        key: "hover",
        get: function get() {
          return this.slideOnHover;
        },
        set: function set(t) {
          this.slideOnHover = "false" !== t.toString().toLowerCase(), this.removeEventListener("mousemove", this.onMouseMove), this.slideOnHover && this.addEventListener("mousemove", this.onMouseMove);
        }
      }, {
        key: "direction",
        get: function get() {
          return this.slideDirection;
        },
        set: function set(t) {
          var _this$firstElement$cl;
          this.slideDirection = t.toString().toLowerCase(), this.slide(0), (_this$firstElement$cl = this.firstElement.classList).remove.apply(_this$firstElement$cl, r), r.includes(this.slideDirection) && this.firstElement.classList.add(this.slideDirection);
        }
      }, {
        key: "connectedCallback",
        value: function connectedCallback() {
          this.hasAttribute("tabindex") || (this.tabIndex = 0), this.addEventListener("dragstart", function (t) {
            return t.preventDefault(), !1;
          }), new ResizeObserver(this.resetDimensions).observe(this), this.setExposure(0), this.keyboard = this.hasAttribute("keyboard") && "disabled" === this.getAttribute("keyboard") ? "disabled" : "enabled", this.addEventListener("keydown", this.onKeyDown), this.addEventListener("keyup", this.onKeyUp), this.addEventListener("focus", this.onFocus), this.addEventListener("blur", this.onBlur), this.addEventListener("touchstart", this.onTouchStart, {
            passive: !0
          }), this.addEventListener("touchmove", this.onTouchMove, {
            passive: !1
          }), this.addEventListener("touchend", this.onTouchEnd), this.addEventListener("mousedown", this.onMouseDown), this.handle = this.hasAttribute("handle") ? this.getAttribute("handle") : this.dragByHandle, this.hover = this.hasAttribute("hover") ? this.getAttribute("hover") : this.slideOnHover, this.direction = this.hasAttribute("direction") ? this.getAttribute("direction") : this.slideDirection, this.resetDimensions(), this.classList.contains(e) || this.classList.add(e);
        }
      }, {
        key: "disconnectedCallback",
        value: function disconnectedCallback() {
          this.transitionTimer && window.clearTimeout(this.transitionTimer);
        }
      }, {
        key: "attributeChangedCallback",
        value: function attributeChangedCallback(t, e, i) {
          "hover" === t && (this.hover = i), "direction" === t && (this.direction = i), "keyboard" === t && (this.keyboard = "disabled" === i ? "disabled" : "enabled");
        }
      }, {
        key: "setExposure",
        value: function setExposure() {
          var t = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
          var e;
          this.exposure = ((e = this.exposure + t) < 0 ? 0 : e > 100 ? 100 : e), this.firstElement.style.setProperty("--exposure", 100 - this.exposure + "%");
        }
      }, {
        key: "slide",
        value: function slide() {
          var t = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
          this.setExposure(t);
          var e = new Event("slide");
          this.dispatchEvent(e);
        }
      }, {
        key: "slideToPage",
        value: function slideToPage(t) {
          "horizontal" === this.slideDirection && this.slideToPageX(t.x), "vertical" === this.slideDirection && this.slideToPageY(t.y);
        }
      }, {
        key: "slideToPageX",
        value: function slideToPageX(t) {
          var e = t - this.getBoundingClientRect().left - window.scrollX;
          this.exposure = e / this.imageWidth * 100, this.slide(0);
        }
      }, {
        key: "slideToPageY",
        value: function slideToPageY(t) {
          var e = t - this.getBoundingClientRect().top - window.scrollY;
          this.exposure = e / this.imageHeight * 100, this.slide(0);
        }
      }, {
        key: "enableTransition",
        value: function enableTransition() {
          var _this2 = this;
          this.firstElement.style.setProperty("--transition-time", "100ms"), this.transitionTimer = window.setTimeout(function () {
            _this2.firstElement.style.setProperty("--transition-time", "var(--default-transition-time)"), _this2.transitionTimer = null;
          }, 100);
        }
      }, {
        key: "startSlideAnimation",
        value: function startSlideAnimation() {
          var _this3 = this;
          var t = null,
            e = this.animationDirection;
          this.firstElement.style.setProperty("--transition-time", "var(--keyboard-transition-time)");
          var _i2 = function i(s) {
            if (0 === _this3.animationDirection || e !== _this3.animationDirection) return;
            null === t && (t = s);
            var o = (s - t) / 16.666666666666668 * _this3.animationDirection;
            _this3.slide(o), setTimeout(function () {
              return window.requestAnimationFrame(_i2);
            }, 0), t = s;
          };
          window.requestAnimationFrame(_i2);
        }
      }, {
        key: "stopSlideAnimation",
        value: function stopSlideAnimation() {
          this.animationDirection = 0, this.firstElement.style.setProperty("--transition-time", "var(--default-transition-time)");
        }
      }], [{
        key: "observedAttributes",
        get: function get() {
          return ["hover", "direction"];
        }
      }]);
    }(h)));
  }();
})();

var dist$1 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

var require$$0 = /*@__PURE__*/getAugmentedNamespace(dist$1);

var _excluded = ["children", "onSlide", "value", "className"];
var dist = createCommonjsModule(function (module, exports) {

  var __createBinding = commonjsGlobal && commonjsGlobal.__createBinding || (Object.create ? function (o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, {
      enumerable: true,
      get: function get() {
        return m[k];
      }
    });
  } : function (o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
  });
  var __setModuleDefault = commonjsGlobal && commonjsGlobal.__setModuleDefault || (Object.create ? function (o, v) {
    Object.defineProperty(o, "default", {
      enumerable: true,
      value: v
    });
  } : function (o, v) {
    o["default"] = v;
  });
  var __importStar = commonjsGlobal && commonjsGlobal.__importStar || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
  };
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.ImgComparisonSlider = void 0;
  if (typeof document !== 'undefined') {
    Promise.resolve().then(function () {
      return __importStar(require$$0);
    });
  }
  exports.ImgComparisonSlider = (0, React.forwardRef)(function (_ref, ref) {
    var children = _ref.children,
      onSlide = _ref.onSlide,
      value = _ref.value,
      className = _ref.className,
      props = _objectWithoutProperties(_ref, _excluded);
    var sliderRef = (0, React.useRef)();
    (0, React.useEffect)(function () {
      if (value !== undefined) {
        sliderRef.current.value = parseFloat(value.toString());
      }
    }, [value, sliderRef]);
    (0, React.useEffect)(function () {
      if (onSlide) {
        // @ts-ignore
        sliderRef.current.addEventListener('slide', onSlide);
      }
    }, []);
    (0, React.useImperativeHandle)(ref, function () {
      return sliderRef.current;
    }, [sliderRef]);
    return (0, React.createElement)('img-comparison-slider', Object.assign({
      "class": className ? "".concat(className, " rendered") : 'rendered',
      // Align tabIndex between the web and React components
      // this code could be removed when
      // https://github.com/WICG/webcomponents/issues/762 is resolved.
      tabIndex: 0,
      ref: sliderRef
    }, props), children);
  });
});

var useStyles = createUseStyles({
  '@global': {
    'img-comparison-slider:focus': {
      outline: 'none'
    }
  },
  divider: {
    '--divider-width': '2px'
  },
  handler: {
    width: 32,
    height: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: CornerRadius.circle,
    background: 'white'
  },
  maxSize: {
    maxWidth: '100%',
    maxHeight: '100%'
  }
}, {
  name: 'media-after-before-slider'
});

var AfterBeforeSlider = function AfterBeforeSlider(_a) {
  var before = _a.before,
    after = _a.after;
  var styles = useStyles();
  return jsxs(dist.ImgComparisonSlider, _assign({
    className: classNames(styles.divider, styles.maxSize)
  }, {
    children: [jsx("img", {
      slot: "first",
      src: before,
      className: styles.maxSize,
      alt: "before change"
    }), jsx("img", {
      slot: "second",
      src: after,
      className: styles.maxSize,
      alt: "after change"
    }), jsxs("div", _assign({
      slot: "handle",
      className: styles.handler
    }, {
      children: [jsx(IconArrowLeftFilled, {
        fill: "black"
      }), jsx(IconArrowRightFilled, {
        fill: "black"
      })]
    }))]
  }));
};

function Media(_a) {
  var className = _a.className,
    imageClassName = _a.imageClassName,
    imageSrc = _a.imageSrc,
    afterImageSrc = _a.afterImageSrc,
    imageSrcLowRes = _a.imageSrcLowRes,
    mobileImageSrc = _a.mobileImageSrc,
    height = _a.height,
    mobileHeight = _a.mobileHeight,
    imageForSlider = _a.imageForSlider,
    videoSrc = _a.videoSrc,
    webmVideoSrc = _a.webmVideoSrc,
    mobileWebmVideoSrc = _a.mobileWebmVideoSrc,
    mobileVideoSrc = _a.mobileVideoSrc,
    alt = _a.alt,
    _b = _a.isControls,
    isControls = _b === void 0 ? false : _b,
    _c = _a.padding,
    padding = _c === void 0 ? 'none' : _c,
    mobilePadding = _a.mobilePadding,
    _d = _a.objectFit,
    objectFit = _d === void 0 ? 'Contain' : _d,
    mobileObjectFit = _a.mobileObjectFit,
    _e = _a.isLazy,
    isLazy = _e === void 0 ? true : _e,
    _f = _a.appendCDNQuery,
    appendCDNQuery = _f === void 0 ? true : _f,
    priority = _a.priority;
  var isMobile = useMobileView().isMobile;
  var root = useStyles$4({
    padding: isMobile && mobilePadding ? mobilePadding : padding,
    objectFit: FittingOptions[isMobile && mobileObjectFit ? mobileObjectFit : objectFit]
  }).root;
  var _g = useState(false),
    isIntersected = _g[0],
    setIsIntersected = _g[1];
  var containerRef = useRef(null);
  var intersectionCallback = useCallback(function (entries) {
    var entry = entries[0];
    if (entry.isIntersecting) {
      setIsIntersected(true);
    }
  }, []);
  useEffect(function () {
    var observer = new IntersectionObserver(intersectionCallback, observerOptions);
    var container = containerRef.current;
    if (container) {
      observer.observe(container);
    }
    return function () {
      if (container) {
        observer.unobserve(container);
      }
    };
  }, [containerRef, intersectionCallback]);
  if (!isLazy && !isIntersected) {
    intersectionCallback([{
      isIntersecting: true
    }]);
  }
  return jsx("div", _assign({
    ref: containerRef,
    className: classNames(root, className),
    "data-testid": testIds.mediaRoot
  }, {
    children: !videoSrc ? afterImageSrc ? jsx(BeforeAfter, {
      before: imageSrc,
      after: afterImageSrc,
      appendCDNQuery: appendCDNQuery,
      padding: padding
    }) : imageForSlider ? jsx(AfterBeforeSlider, {
      before: imageSrc,
      after: imageForSlider
    }) : jsx(BaseImage$1, {
      imageClassName: imageClassName,
      imageSrc: imageSrc,
      imageSrcLowRes: imageSrcLowRes,
      mobileImageSrc: mobileImageSrc,
      height: height,
      mobileHeight: mobileHeight,
      padding: padding,
      alt: alt,
      isLazy: isLazy,
      objectFit: objectFit,
      appendCDNQuery: appendCDNQuery,
      priority: priority
    }) : jsx(BaseVideo$1, {
      isControls: isControls,
      videoSrc: videoSrc,
      mobileVideoSrc: mobileVideoSrc,
      poster: imageSrc,
      isIntersected: isIntersected,
      webmVideoSrc: webmVideoSrc,
      mobileWebmVideoSrc: mobileWebmVideoSrc
    })
  }));
}

export { AfterBeforeSlider as A, BaseImage$1 as B, FittingOptions as F, IMAGE_TYPES as I, Media as M, BaseVideo$1 as a, bleedModeOptions as b, observerOptions as o };
//# sourceMappingURL=Media-e137048e.js.map
